import { morphoPools } from '@1delta/data-sdk'
import { MulticallRetryFunction } from '../../../types'
import { toGenericPriceKey, toOracleKey } from '../../../assets'
import { createMarketUid } from '../../../utils'

/**
 * Morpho Blue does NOT store a per-market collateral total.
 *
 * `Market` carries `totalSupplyAssets / totalSupplyShares / totalBorrowAssets /
 * totalBorrowShares / lastUpdate / fee` and nothing else — collateral lives
 * only in `position(id, user).collateral`. That is why the API path can serve
 * `collateralAssets` (Morpho's indexer sums the positions) while the on-chain
 * path used to publish a hardcoded `0` for every collateral row, on every chain
 * `blue-api` and Goldsky do not index (Plume, Flare, Citrea, Sonic, Scroll,
 * Gnosis, Avalanche, XDC, MegaETH, Kaia, Cronos, Morph, Mode, Linea, …).
 *
 * A zero there is not "no data", it renders as an EMPTY market: `$0` TVL and
 * `$0` liquidity next to a live rate — and the hourly recorder stores the same
 * zero, so the row's whole history plots flat at zero too.
 *
 * What the chain CAN answer exactly is the singleton's token balance. Morpho
 * holds exactly two things per token: collateral posted against it, and the
 * idle (un-borrowed) liquidity of markets that use it as the LOAN token —
 * accrued fees are minted as supply SHARES, never as tokens. So
 *
 *   collateral(T) = balanceOf(T, morpho) − Σ (supply − borrow) over loan markets of T
 *
 * is the exact amount of `T` sitting as collateral across the whole instance.
 *
 * Splitting that across markets is the part the chain cannot answer. When `T`
 * is the collateral of exactly ONE market the sum IS that market's total and is
 * published; when several markets share it (typically the same pair at two
 * LLTVs) the split is genuinely unknown, and this deliberately leaves those
 * rows untouched rather than inventing a proportional guess — collateral does
 * not track debt here (Plume holds 3.99M nALPHA against ~$3.5k of pUSD debt, so
 * a debt-weighted split would be wrong by three orders of magnitude). The only
 * exact source for those is position indexing; see the README.
 */

const ERC20_BALANCE_OF_ABI = [
  {
    inputs: [{ name: 'account', type: 'address' }],
    name: 'balanceOf',
    outputs: [{ name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
] as const

const MORPHO_BLUE_BUCKET_PREFIX = 'MORPHO_BLUE_'

interface MarketRef {
  bucket: string
  /** Row key of this market's collateral side, as the converter minted it. */
  collateralUid: string
  collateral: string
  loan: string
  collateralDecimals: number
  /** Idle loan liquidity of this market, in LOAN token units. */
  loanIdle: number
}

function formatBalance(raw: unknown, decimals: number): number | undefined {
  if (raw === undefined || raw === null || raw === '0x') return undefined
  let value: bigint
  try {
    value = BigInt(raw as any)
  } catch {
    return undefined
  }
  return Number(value) / 10 ** decimals
}

/**
 * Fill in `totalDeposits` / `totalDepositsUSD` (and the matching liquidity
 * fields, which for a collateral side equal the deposits — the API path shapes
 * them the same way) on the collateral rows of on-chain-fetched Morpho Blue
 * buckets.
 *
 * Mutates `result` in place and returns it. Never throws: a failed balance read
 * leaves the row exactly as the converter produced it.
 */
export async function enrichMorphoCollateralTotals(
  chainId: string,
  result: { [lender: string]: any },
  prices: { [asset: string]: number },
  multicallRetry: MulticallRetryFunction,
): Promise<{ [lender: string]: any }> {
  const morpho = morphoPools()?.MORPHO_BLUE?.[chainId]
  if (!morpho) return result

  const markets: MarketRef[] = []

  for (const [bucket, entry] of Object.entries(result)) {
    if (!bucket.startsWith(MORPHO_BLUE_BUCKET_PREFIX)) continue
    const market = (entry as any)?.params?.market
    const rows = (entry as any)?.data
    if (!market || !rows) continue

    const collateral = String(market.collateralAddress ?? '').toLowerCase()
    const loan = String(market.loanAddress ?? '').toLowerCase()
    if (!collateral || !loan) continue

    // Rows are addressed by the uid the converter minted, never by scanning
    // for `underlying` — the two sides of a market are only distinguishable by
    // their key when a market lends and collateralizes the same token.
    const loanRow = rows[createMarketUid(chainId, bucket, loan)]
    const supply = Number(loanRow?.totalDeposits ?? 0)
    const borrow = Number(loanRow?.totalDebt ?? 0)
    const loanIdle =
      Number.isFinite(supply) && Number.isFinite(borrow) ? supply - borrow : 0

    markets.push({
      bucket,
      collateralUid: createMarketUid(chainId, bucket, collateral),
      collateral,
      loan,
      collateralDecimals: Number(market.collateralDecimals ?? 18),
      loanIdle,
    })
  }

  if (markets.length === 0) return result

  // Only tokens that back exactly one market can be attributed. Reading the
  // others would cost an RPC call for a number nothing may consume.
  const marketsByCollateral = new Map<string, MarketRef[]>()
  for (const m of markets) {
    const list = marketsByCollateral.get(m.collateral)
    if (list) list.push(m)
    else marketsByCollateral.set(m.collateral, [m])
  }
  const soleCollateralTokens = [...marketsByCollateral.entries()]
    .filter(([, list]) => list.length === 1)
    .map(([token]) => token)

  if (soleCollateralTokens.length === 0) return result

  let balances: any[]
  try {
    balances = await multicallRetry({
      chain: chainId,
      calls: soleCollateralTokens.map((token) => ({
        address: token,
        functionName: 'balanceOf',
        args: [morpho],
      })),
      abi: soleCollateralTokens.map(() => ERC20_BALANCE_OF_ABI),
      allowFailure: true,
    })
  } catch {
    return result
  }

  // Idle loan liquidity per token, summed over every market that lends it —
  // subtracted because Morpho's balance holds both legs.
  const idleByToken = new Map<string, number>()
  for (const m of markets) {
    idleByToken.set(m.loan, (idleByToken.get(m.loan) ?? 0) + m.loanIdle)
  }

  soleCollateralTokens.forEach((token, i) => {
    const m = marketsByCollateral.get(token)![0]
    const balance = formatBalance(balances?.[i], m.collateralDecimals)
    if (balance === undefined) return

    const total = balance - (idleByToken.get(token) ?? 0)
    // Rounding on the idle leg can only push this a few wei negative; a real
    // negative is impossible and would mean the balance read is not this token.
    if (!Number.isFinite(total) || total < 0) return

    const row = result[m.bucket]?.data?.[m.collateralUid]
    if (!row) return

    const priceKey =
      toOracleKey(row.asset?.assetGroup ?? null) ||
      toGenericPriceKey(token, chainId)
    const price = prices[priceKey] ?? 0
    const usd = total * price

    row.totalDeposits = total
    row.totalLiquidity = total
    row.borrowLiquidity = total
    row.totalDepositsUSD = usd
    row.totalLiquidityUSD = usd
    row.borrowLiquidityUSD = usd
  })

  return result
}
